/**
 * A plan's Activity tab — its Staffing Concerns (RP-34) and its comments in one timeline,
 * newest first, with a comment box on top — and the Raise Concern action.
 *
 * Posting here makes a Comment, never a Concern: raising a Concern is its own, explicit
 * action, because it notifies the account team and is tracked until it is resolved. Each
 * entry is tagged Concern or Comment so the two stay distinguishable. Line notes appear too,
 * labelled with their line.
 *
 * A Concern is the RM's "I cannot staff this as planned", attached to the plan or one Plan
 * Line. It is a conversation, not a status: the plan's Sales status never moves. Ticking
 * "Blocking" is what Reject used to be — the plan goes back to its author (Resourcing:
 * Returned) and cannot be marked Staffed until the account team acknowledges it.
 *
 * Who can do what comes from the server (`canRaise`, `canRaiseBlocking`, each Concern's
 * `canAcknowledge` / `canResolve`, and the comments' `canWrite`), so the buttons and the
 * endpoints agree.
 */

import { AlertTriangle, CheckCircle2, MessageSquare, MessageSquareWarning } from "lucide-react";
import { type ReactNode, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAddPlanComment, usePlanComments } from "@/hooks/use-plan-comments";
import {
  useAcknowledgePlanConcern,
  usePlanConcerns,
  useRaisePlanConcern,
  useResolvePlanConcern,
} from "@/hooks/use-plan-concerns";
import { mergePlanActivity } from "@/lib/plan-activity";
import { cn } from "@/lib/utils";
import {
  PLAN_CONCERN_TYPE_LABELS,
  PLAN_CONCERN_TYPES,
  type PlanConcernType,
  type PlanConcernView,
} from "@/schemas/plan-concerns";
import type {
  PlanCommentView,
  PlanLineView,
  ResourcePlanDetailView,
} from "@/schemas/resource-plans";

const WHOLE_PLAN = "__plan__";
const MAX_COMMENT = 2000;

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Date and time, so entries from the same day read in order. */
function formatWhenAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function lineLabel(line: PlanLineView): string {
  return line.suggestedResourceName === null
    ? line.laborCategoryName
    : `${line.laborCategoryName} (${line.suggestedResourceName})`;
}

// ── Header badge ──────────────────────────────────────────────────────────────

/** "2 open Concerns" in the plan header; red while a Blocking one waits on the account team. */
export function PlanConcernsBadge({
  plan,
  onOpen,
}: {
  plan: ResourcePlanDetailView;
  onOpen: () => void;
}) {
  const summary = plan.concerns;
  if (summary === undefined || summary.unresolved === 0) return null;
  const blocking = summary.blockingOpen > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-opacity hover:opacity-80",
        blocking
          ? "bg-error-100 text-error-700 dark:text-error-400"
          : "bg-warning-100 text-warning-700 dark:text-warning-400",
      )}
      title={
        blocking
          ? "A Blocking Staffing Concern is waiting on the account team. The plan cannot be marked Staffed until it is acknowledged."
          : "Open Staffing Concerns from the Resource Manager."
      }
    >
      <MessageSquareWarning className="h-3.5 w-3.5" aria-hidden />
      {summary.unresolved} open Concern{summary.unresolved === 1 ? "" : "s"}
      {blocking && " · blocking"}
    </button>
  );
}

// ── Raise ─────────────────────────────────────────────────────────────────────

/** The header's "Raise Concern" action — RM / ADMIN. Replaces the old Reject button. */
export function RaiseConcernButton({ plan }: { plan: ResourcePlanDetailView }) {
  const { data } = usePlanConcerns(plan.id);
  const [open, setOpen] = useState(false);
  if (data?.canRaise !== true) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <MessageSquareWarning className="h-3.5 w-3.5" />
        Raise Concern
      </Button>
      {open && (
        <RaiseConcernDialog
          plan={plan}
          canRaiseBlocking={data.canRaiseBlocking}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RaiseConcernDialog({
  plan,
  canRaiseBlocking,
  onClose,
}: {
  plan: ResourcePlanDetailView;
  canRaiseBlocking: boolean;
  onClose: () => void;
}) {
  const raise = useRaisePlanConcern(plan.id);
  const [type, setType] = useState<PlanConcernType>("NoAvailableResource");
  const [lineId, setLineId] = useState<string>(WHOLE_PLAN);
  const [message, setMessage] = useState("");
  const [blocking, setBlocking] = useState(false);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Raise a Staffing Concern</DialogTitle>
          <DialogDescription>
            {`Tells ${plan.ownerName ?? "the plan's Author"} and the Account's owners what stands in the way of staffing "${plan.name}". The plan's status does not change.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="concern-type">Type</Label>
              <Select
                value={type}
                onValueChange={(v) => {
                  const picked = PLAN_CONCERN_TYPES.find((t) => t === v);
                  if (picked !== undefined) setType(picked);
                }}
              >
                <SelectTrigger id="concern-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_CONCERN_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {PLAN_CONCERN_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="concern-line">About</Label>
              <Select value={lineId} onValueChange={setLineId}>
                <SelectTrigger id="concern-line" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={WHOLE_PLAN}>The whole plan</SelectItem>
                  {plan.lines.map((line) => (
                    <SelectItem key={line.id} value={line.id}>
                      {lineLabel(line)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="concern-message">What is the problem?</Label>
            <Textarea
              id="concern-message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
            />
          </div>

          {canRaiseBlocking && (
            <div className="flex items-start gap-2.5 rounded-md border border-border p-3">
              <Checkbox
                id="concern-blocking"
                checked={blocking}
                onCheckedChange={(checked) => setBlocking(checked === true)}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label htmlFor="concern-blocking">Blocking — reject the plan</Label>
                <p className="text-xs text-muted-foreground">
                  Sets Resourcing to Rejected and logs it in the Rejected Plans report. The plan
                  cannot be marked Staffed until the account team acknowledges this Concern.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={raise.isPending}>
            Cancel
          </Button>
          <Button
            variant={blocking ? "destructive" : "default"}
            disabled={message.trim() === "" || raise.isPending}
            onClick={() =>
              raise.mutate(
                {
                  type,
                  message: message.trim(),
                  planLineId: lineId === WHOLE_PLAN ? null : lineId,
                  isBlocking: blocking,
                },
                { onSuccess: onClose },
              )
            }
          >
            {raise.isPending ? "Raising…" : blocking ? "Raise and reject plan" : "Raise Concern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── The tab ───────────────────────────────────────────────────────────────────

export function PlanActivityPanel({ plan }: { plan: ResourcePlanDetailView }) {
  const concerns = usePlanConcerns(plan.id);
  const comments = usePlanComments(plan.id, {}, true);
  const [acting, setActing] = useState<{
    concern: PlanConcernView;
    action: "acknowledge" | "resolve";
  } | null>(null);

  if (concerns.isLoading || comments.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (concerns.isError || concerns.data === undefined) {
    return (
      <ErrorState
        message={
          concerns.error instanceof Error
            ? concerns.error.message
            : "Could not load Staffing Concerns."
        }
        onRetry={concerns.refetch}
      />
    );
  }
  if (comments.isError || comments.data === undefined) {
    return (
      <ErrorState
        message={
          comments.error instanceof Error ? comments.error.message : "Could not load comments."
        }
        onRetry={comments.refetch}
      />
    );
  }

  const items = mergePlanActivity(concerns.data.concerns, comments.data.data);

  return (
    <div className="space-y-4 pt-4">
      {(comments.data.canWrite || concerns.data.canRaise) && (
        <CommentComposer
          planId={plan.id}
          canWrite={comments.data.canWrite}
          raise={concerns.data.canRaise ? <RaiseConcernButton plan={plan} /> : null}
        />
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No activity yet"
          description="Comments and Staffing Concerns about this plan show up here, newest first. Post a comment above, or raise a Concern when the plan can't be staffed as written."
        />
      ) : (
        <ol className="space-y-3">
          {items.map((item) =>
            item.kind === "concern" ? (
              <ConcernCard
                key={`concern:${item.concern.id}`}
                concern={item.concern}
                onAcknowledge={() => setActing({ concern: item.concern, action: "acknowledge" })}
                onResolve={() => setActing({ concern: item.concern, action: "resolve" })}
              />
            ) : (
              <ActivityCommentCard key={`comment:${item.comment.id}`} comment={item.comment} />
            ),
          )}
        </ol>
      )}

      {acting !== null && (
        <ConcernNoteDialog
          planId={plan.id}
          concern={acting.concern}
          action={acting.action}
          onClose={() => setActing(null)}
        />
      )}
    </div>
  );
}

/** Posts a Comment on the whole plan. Raise Concern sits beside it for those who may. */
function CommentComposer({
  planId,
  canWrite,
  raise,
}: {
  planId: string;
  canWrite: boolean;
  raise: ReactNode;
}) {
  const add = useAddPlanComment(planId);
  const [draft, setDraft] = useState("");
  const trimmed = draft.trim();

  if (!canWrite) return <div className="flex justify-end">{raise}</div>;

  // The button row sits outside the <form>: Raise Concern opens its own dialog and must
  // never submit the comment, so only "Post comment" is tied to the form (by id).
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
      <form
        id="activity-comment-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed === "") return;
          add.mutate({ body: trimmed }, { onSuccess: () => setDraft("") });
        }}
      >
        <Label htmlFor="activity-comment" className="sr-only">
          New comment
        </Label>
        <Textarea
          id="activity-comment"
          value={draft}
          maxLength={MAX_COMMENT}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Add a comment about this plan…"
          rows={3}
        />
      </form>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          Internal only — never in the client export. {draft.length}/{MAX_COMMENT}
        </span>
        <div className="flex items-center gap-2">
          {raise}
          <Button
            type="submit"
            form="activity-comment-form"
            size="sm"
            disabled={trimmed === "" || add.isPending}
          >
            {add.isPending ? "Posting…" : "Post comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function KindTag({ kind }: { kind: "concern" | "comment" }) {
  return kind === "concern" ? (
    <span className="inline-flex items-center gap-1 rounded bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-700 dark:text-warning-400">
      <MessageSquareWarning className="h-3 w-3" aria-hidden />
      Concern
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <MessageSquare className="h-3 w-3" aria-hidden />
      Comment
    </span>
  );
}

function ActivityCommentCard({ comment }: { comment: PlanCommentView }) {
  return (
    <li className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <KindTag kind="comment" />
        <span className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">
            {comment.author?.name ?? "Former user"}
          </span>{" "}
          · {formatWhenAt(comment.createdAt)}
          {comment.lineLabel !== null && ` · on ${comment.lineLabel}`}
        </span>
      </div>
      {comment.body === null ? (
        <p className="mt-3 text-sm italic text-muted-foreground">
          Comment removed{comment.deletedByName !== null && ` by ${comment.deletedByName}`}
        </p>
      ) : (
        <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{comment.body}</p>
      )}
    </li>
  );
}

function ConcernStatusChip({ concern }: { concern: PlanConcernView }) {
  if (concern.status === "Resolved") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        <CheckCircle2 className="h-3 w-3" aria-hidden />
        Resolved
      </span>
    );
  }
  if (concern.status === "Acknowledged") {
    return (
      <span className="rounded bg-info-100 px-2 py-0.5 text-xs font-medium text-info-700">
        Acknowledged
      </span>
    );
  }
  return (
    <span
      className={cn(
        "rounded px-2 py-0.5 text-xs font-medium",
        concern.isBlocking
          ? "bg-error-100 text-error-700 dark:text-error-400"
          : "bg-warning-100 text-warning-700 dark:text-warning-400",
      )}
    >
      {concern.isBlocking ? "Open · blocking" : "Open"}
    </span>
  );
}

function ConcernCard({
  concern,
  onAcknowledge,
  onResolve,
}: {
  concern: PlanConcernView;
  onAcknowledge: () => void;
  onResolve: () => void;
}) {
  return (
    <li
      className={cn(
        "rounded-lg border bg-card p-4",
        concern.status === "Resolved" ? "border-border opacity-80" : "border-border",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <KindTag kind="concern" />
            <span className="text-sm font-semibold text-foreground">
              {PLAN_CONCERN_TYPE_LABELS[concern.type]}
            </span>
            <ConcernStatusChip concern={concern} />
            {concern.returnedPlan && (
              <span className="text-xs text-muted-foreground">Rejected this plan</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {concern.planLineLabel === null ? "Whole plan" : concern.planLineLabel} · raised by{" "}
            {concern.raisedByName ?? "a former user"} on {formatWhenAt(concern.raisedAt)}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {concern.canAcknowledge && (
            <Button size="sm" onClick={onAcknowledge}>
              Acknowledge
            </Button>
          )}
          {concern.canResolve && (
            <Button size="sm" variant="outline" onClick={onResolve}>
              Resolve
            </Button>
          )}
        </div>
      </div>

      <p className="mt-3 whitespace-pre-wrap text-sm text-foreground">{concern.message}</p>

      {concern.acknowledgedAt !== null && (
        <ConcernNote
          label={`Acknowledged by ${concern.acknowledgedByName ?? "a former user"} on ${formatWhen(concern.acknowledgedAt)}`}
          note={concern.acknowledgementNote}
        />
      )}
      {concern.resolvedAt !== null && (
        <ConcernNote
          label={`Resolved by ${concern.resolvedByName ?? "a former user"} on ${formatWhen(concern.resolvedAt)}`}
          note={concern.resolutionNote}
        />
      )}
      {concern.isBlocking && concern.status === "Open" && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-error-700 dark:text-error-400">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          Waiting on the account team. The plan cannot be marked Staffed until this is acknowledged.
        </p>
      )}
    </li>
  );
}

function ConcernNote({ label, note }: { label: string; note: string | null }) {
  return (
    <div className="mt-3 border-l-2 border-border pl-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      {note !== null && <p className="whitespace-pre-wrap text-sm text-foreground">{note}</p>}
    </div>
  );
}

function ConcernNoteDialog({
  planId,
  concern,
  action,
  onClose,
}: {
  planId: string;
  concern: PlanConcernView;
  action: "acknowledge" | "resolve";
  onClose: () => void;
}) {
  const acknowledge = useAcknowledgePlanConcern(planId);
  const resolve = useResolvePlanConcern(planId);
  const mutation = action === "acknowledge" ? acknowledge : resolve;
  const [note, setNote] = useState("");

  const title = action === "acknowledge" ? "Acknowledge this Concern" : "Resolve this Concern";
  const description =
    action === "acknowledge"
      ? "Tells the Resource Manager the account team has seen it. Once acknowledged, the plan can be picked back up and marked Staffed."
      : concern.returnedPlan
        ? "Closes the Concern and picks the plan back up: its Resourcing status moves from Rejected to In Review."
        : "Closes the Concern. The account team is notified.";

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="concern-note">Note (optional)</Label>
          <Textarea
            id="concern-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate({ concernId: concern.id, note: note.trim() }, { onSuccess: onClose })
            }
          >
            {action === "acknowledge" ? "Acknowledge" : "Resolve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
